import { FocusTrap } from "@ark-ui/react";
import classnames from "classnames";
import { useCallback, useRef, MouseEvent, useMemo } from "react";
import { Link } from "react-router-dom";

import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";
import { useDashboardData } from "@/features/shared/hooks";
import useBodyScrollLock from "@/features/shared/hooks/use-body-scroll-lock";
import useMediaQuery from "@/features/shared/hooks/use-media-query";
import useOnKeyDown from "@/features/shared/hooks/use-on-key-down";
import { NavigationCollapsedContextProvider } from "@/features/dashboard/components/navigation/navigation-state";
import useLayoutStore from "@/store/useLayoutStore";
import Logo from "@/components/logo/logo";

import NavigationDarkModeToggle from "./navigation-dark-mode-toggle";
import NavigationLocaleDebugToggle from "./navigation-locale-debug-toggle";
import NavigationElements from "./navigation-elements";
import NavigationAuthButton from "./navigation-auth-button";
import NavigationMobileHeader from "./navigation-mobile-header";
import NavigationSettings from "./navigation-settings";
import NavigationDashboardSelector from "./navigation-dashboard-selector";
import styles from "./navigation.module.css";

/** Same as `@media (--md)` in `src/styles/breakpoints.css` — desktop nav, no overlay lock. */
const MD_MIN_WIDTH_MEDIA = "(min-width: 800px)";

export default function Navigation() {
  const { navigationElements } = useDashboardData();

  const [{ isFullscreen, isMenuCollapsed, isMobileMenuOpen }, layoutStore] = useLayoutStore(
    (s) => s.context,
  );

  useOnKeyDown(["Escape"], () => layoutStore.trigger.setMobileMenuOpen({ value: false }));

  const isMdViewport = useMediaQuery(MD_MIN_WIDTH_MEDIA);
  useBodyScrollLock(isMobileMenuOpen && !isMdViewport);

  const toggleCollapse = useCallback(
    (collapse?: boolean) => {
      if (isFullscreen) {
        layoutStore.trigger.setFullscreen({ value: false });
        setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
        return;
      }
      if (collapse !== undefined) {
        layoutStore.trigger.setMenuCollapse({ value: collapse });
      } else {
        layoutStore.trigger.toggleMenuCollapse();
      }
      setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
    },
    [layoutStore, isFullscreen],
  );

  const toggleMobileMenu = useCallback(() => {
    layoutStore.trigger.toggleMobileMenu();
  }, [layoutStore]);

  const handleWrapperClick = useCallback(
    (e: MouseEvent) => {
      if (isMobileMenuOpen && e.target === e.currentTarget) {
        layoutStore.trigger.setMobileMenuOpen({ value: false });
      }
    },
    [layoutStore],
  );

  const isCollapsed = useMemo(() => {
    return isFullscreen || isMenuCollapsed;
  }, [isFullscreen, isMenuCollapsed]);

  const mobileCloseToggleRef = useRef<HTMLButtonElement>(null);

  return (
    <NavigationCollapsedContextProvider value={isCollapsed}>
      <FocusTrap
        disabled={!isMobileMenuOpen}
        initialFocus={() => mobileCloseToggleRef.current}
        returnFocusOnDeactivate
      >
        <NavigationMobileHeader toggleMobileMenu={toggleMobileMenu} />

        <div
          className={classnames(styles.wrapper, {
            [styles.collapsed]: isCollapsed,
            [styles.mobileOpen]: isMobileMenuOpen,
          })}
          onClick={handleWrapperClick}
        >
          <Button
            onClick={() => toggleCollapse()}
            variant="primary"
            className={styles.toggleButton}
          >
            {isCollapsed ? (
              <Icon name="arrow-right-to-line" />
            ) : (
              <Icon name="arrow-left-to-line" />
            )}
          </Button>

          <div className={classnames(styles.navigation)}>
            <div className={styles.navigationHeader}>
              <Button
                onClick={toggleMobileMenu}
                variant="primary"
                className={styles.mobileCloseButton}
                ref={mobileCloseToggleRef}
              >
                <Icon name="x" />
              </Button>

              <Link to="/" className={styles.logo}>
                <Logo />
              </Link>
            </div>

            <NavigationDashboardSelector setCollapsed={toggleCollapse} />

            <NavigationElements navigationElements={navigationElements} />

            <NavigationSettings />
            <NavigationDarkModeToggle />
            {import.meta.env.DEV && <NavigationLocaleDebugToggle />}
            <NavigationAuthButton />
          </div>
        </div>
      </FocusTrap>
    </NavigationCollapsedContextProvider>
  );
}
